import { Button } from '@wonderschool/common-base-ui';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import appDownloadQr from '../assets/images/app-download-qr.png';
import downloadAppleStoreBadge from '../assets/images/download-apple-store-badge.svg?url';
import downloadGooglePlayBadge from '../assets/images/download-google-play-badge2.png';
import OrgQrPrintCard from '../Components/Shared/OrgQrPrintCard';
import PageHeader from '../Components/Shared/PageHeader';
import { getTitlePrefix } from '../config/env';
import { useOrganization } from '../hooks/useOrganizations';
import { paths } from '../navigation/paths';
import { getOrganizationDevices } from '../redux/actions/deviceActions';
import { useAllStudents } from '../students/studentsHooks';
import StepCard from './CheckInCheckOut/StepCard';

const APPLE_STORE_URL = 'https://apps.apple.com/us/app/wonderschool-2-0/id1560074981';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.moxitinc.wonderschool';

// Steps 1 and 4 auto-detect completion from data. Steps 2 (download the app) and
// 3 (generate the QR code) have no server signal, so we let the provider mark them
// done and remember that locally, keyed per organization. Swap for a persisted
// onboarding-progress record if we want it to follow the account across devices.
type ManualStepKey = 'download' | 'qr';
const manualDoneStorageKey = (organizationId: string) => `cico-onboarding:${organizationId}`;

const CheckInCheckOutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organization = useOrganization();
  const organizationId: string | undefined = organization?.id;

  const title = t('Check In Check Out');

  // Step 1 — has the provider added any students yet?
  const students = useAllStudents();
  const studentCount: number = students?.list?.length ?? 0;
  const hasStudents = studentCount > 0;

  // Step 4 — has the provider registered any shared devices yet?
  const devices = useSelector((state: any) => state.devices);
  const deviceCount: number = devices?.list?.length ?? 0;
  const hasDevice = deviceCount > 0;

  // Load devices so we can auto-detect step 4 (they are not loaded app-wide).
  useEffect(() => {
    if (organizationId) dispatch(getOrganizationDevices(organizationId) as any);
  }, [dispatch, organizationId]);

  // Manually-completed steps (download app, generate QR), persisted per org.
  const [manualDone, setManualDone] = useState<Record<ManualStepKey, boolean>>({ download: false, qr: false });

  useEffect(() => {
    if (!organizationId) return;
    try {
      const raw = window.localStorage.getItem(manualDoneStorageKey(organizationId));
      if (raw) setManualDone((prev) => ({ ...prev, ...JSON.parse(raw) }));
    } catch {
      // ignore malformed storage
    }
  }, [organizationId]);

  const toggleManual = useCallback(
    (key: ManualStepKey) => {
      setManualDone((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        if (organizationId) {
          try {
            window.localStorage.setItem(manualDoneStorageKey(organizationId), JSON.stringify(next));
          } catch {
            // ignore storage write failures
          }
        }
        return next;
      });
    },
    [organizationId]
  );

  // Only the three required steps count toward completion. The shared device is an
  // optional add-on and is intentionally excluded from the progress total.
  const steps = useMemo(
    () => [hasStudents, manualDone.download, manualDone.qr],
    [hasStudents, manualDone.download, manualDone.qr]
  );
  const completedCount = steps.filter(Boolean).length;
  const totalSteps = steps.length;
  const allComplete = completedCount === totalSteps;

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <Helmet>
        <title>
          {getTitlePrefix()}
          {`${title} - Wonderschool`}
        </title>
      </Helmet>
      <PageHeader pageName={title} classes="check-in-check-out-page" />

      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <p className="text-base leading-relaxed text-gray-700">{t('checkInCheckOut.intro')}</p>
        </div>

        {/* Progress summary */}
        <div className="mb-8 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">
              {allComplete ? t('checkInCheckOut.allSet') : t('checkInCheckOut.finishSetup')}
            </span>
            <span className="text-sm font-medium text-gray-500">
              {t('checkInCheckOut.progressCount', { completed: completedCount, total: totalSteps })}
            </span>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-green-500 transition-all"
              style={{ width: `${(completedCount / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <ol className="list-none">
          {/* Step 1 — Add a student */}
          <StepCard
            index={1}
            title={t('checkInCheckOut.step1Title')}
            complete={hasStudents}
            description={
              hasStudents
                ? t('checkInCheckOut.step1DescComplete', { count: studentCount })
                : t('checkInCheckOut.step1DescIncomplete')
            }
            actions={
              hasStudents ? (
                <Button onClick={() => navigate(paths.students)}>{t('checkInCheckOut.manageStudents')}</Button>
              ) : (
                <Button primary onClick={() => navigate(paths.students)}>
                  {t('checkInCheckOut.addStudents')}
                </Button>
              )
            }
          />

          {/* Step 2 — Download the app */}
          <StepCard
            index={2}
            title={t('checkInCheckOut.step2Title')}
            complete={manualDone.download}
            description={t('checkInCheckOut.step2Desc')}
            actions={
              <Button onClick={() => toggleManual('download')}>
                {manualDone.download ? t('checkInCheckOut.markAsNotDone') : t('checkInCheckOut.markAsDone')}
              </Button>
            }
          >
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="flex flex-none flex-col items-center gap-1 self-start">
                <img src={appDownloadQr} alt="Scan to download the Wonderschool app" className="h-32 w-32" />
                <span className="text-xs text-gray-500">{t('wonderschoolApp.scanToDownload')}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={APPLE_STORE_URL} target="_blank" rel="noreferrer">
                  <img
                    style={{ height: '40px', width: 'auto' }}
                    src={downloadAppleStoreBadge}
                    alt="Download on the App Store"
                  />
                </a>
                <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer">
                  <img
                    style={{ height: '40px', width: 'auto' }}
                    src={downloadGooglePlayBadge}
                    alt="Get it on Google Play"
                  />
                </a>
              </div>
            </div>
          </StepCard>

          {/* Step 3 — Generate the QR code */}
          <StepCard
            index={3}
            isLast
            title={t('checkInCheckOut.step3Title')}
            complete={manualDone.qr}
            description={t('checkInCheckOut.step3Desc')}
            actions={
              <>
                <Button onClick={() => toggleManual('qr')}>
                  {manualDone.qr ? t('checkInCheckOut.markAsNotDone') : t('checkInCheckOut.markAsDone')}
                </Button>
                <Button primary onClick={() => window.print()}>
                  {t('checkInCheckOut.printQrCode')}
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="flex flex-none items-center justify-center self-start rounded-lg border border-gray-200 bg-white p-3">
                {/* level matches OrgQrPrintCard so this preview shows the code density that prints. */}
                {organizationId ? (
                  <QRCodeSVG value={organizationId} size={128} level="Q" fgColor="#28325A" includeMargin />
                ) : (
                  <div className="h-32 w-32 animate-pulse rounded bg-gray-100" />
                )}
              </div>
              <ol className="ml-4 list-decimal space-y-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                <li>{t('qrCode.step1')}</li>
                <li>{t('qrCode.step2')}</li>
                <li>{t('qrCode.step3')}</li>
                <li>{t('qrCode.step4')}</li>
                <li>{t('qrCode.step5')}</li>
              </ol>
              {/* Hidden on screen; the only thing that shows when the page is printed. */}
              {organizationId && <OrgQrPrintCard value={organizationId} organizationName={organization?.name} />}
            </div>
          </StepCard>

          {/* Optional add-on — Set up a shared device (not counted in the 3-step flow) */}
          <StepCard
            optional
            isLast
            title={t('checkInCheckOut.step4Title')}
            complete={hasDevice}
            description={
              hasDevice
                ? t('checkInCheckOut.step4DescComplete', { count: deviceCount })
                : t('checkInCheckOut.step4DescIncomplete')
            }
            actions={
              hasDevice ? (
                <Button onClick={() => navigate(paths.devices)}>{t('checkInCheckOut.manageDevices')}</Button>
              ) : (
                <Button onClick={() => navigate(paths.devices)}>{t('checkInCheckOut.setUpSharedDevice')}</Button>
              )
            }
          />
        </ol>
      </div>
    </div>
  );
};

export default CheckInCheckOutPage;
